import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { bbox as turfBbox } from '@turf/turf';
import type { FeatureCollection as GeoJsonFeatureCollection } from 'geojson';
import { Feature, GeoJSON, GeoJsonProperties, Geometry } from 'geojson';
import { ExpressionFilterSpecification, GeoJSONSource, LayerSpecification, LngLatLike } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { MapBaseSource, MapFieldInterventionSource } from '@connect-services/client/components/map/map.component';
import { BYPASS_INTERCEPTOR } from '@connect-services/client/interceptors/http-errors.interceptor';
import { AssertService } from '@connect-services/client/services/assert.service';
import { FilterEntitiesDto } from '@connect-services/client/services/field-intervention-entities-filter.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { DrawSourcesOptions } from '@connect-services/client/services/map-source.service';
import {
    FeatureCollection,
    FieldIntervention,
    FieldInterventionEntitiesService,
    MapboxStyle,
} from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class MapFieldInterventionService extends MapBaseService {
    public readonly defaultStyleFilterByLayer: Map<string, ExpressionFilterSpecification | undefined> = new Map<
        string,
        ExpressionFilterSpecification | undefined
    >();

    private drawGeneration = 0;
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly mapLayerService = inject(MapLayerService);

    public cancelPendingDraws(): void {
        this.drawGeneration += 1;
    }

    public async drawFieldIntervention(source: MapFieldInterventionSource, options: DrawSourcesOptions): Promise<void> {
        if (!this.map) {
            return;
        }

        await this.waitForStyleLoaded();

        if (!this.map) {
            return;
        }

        const isSourceAlreadyInitialized = !!this.map.getSource(source.sourceId);

        await this.drawFieldInterventionSource(
            source.sourceId,
            source.style,
            isSourceAlreadyInitialized ? this.isSourceVisible(source) : source.isVisible,
            source.data,
            options,
            source.layerFilter,
        );
    }

    public async filterFieldInterventionEntitiesOnMap(
        layerIds: string[],
        sources: MapBaseSource<unknown>[] = [],
        paramsFilter?: FilterEntitiesDto,
    ): Promise<void> {
        if (!paramsFilter || !this.isMapPresent()) {
            return;
        }

        if (!sources.every((source) => !!this.map!.getSource(source.sourceId))) {
            this.map.once('sourcedata', () => {
                void this.filterFieldInterventionEntitiesOnMap(layerIds, sources, paramsFilter);
            });

            return;
        }

        this.applyFieldInterventionFilter(layerIds, paramsFilter);

        if (paramsFilter.forms?.length || paramsFilter.people?.length) {
            await this.setPropertiesFilter(sources, paramsFilter);
        }
    }

    public isSourceVisible(source: MapFieldInterventionSource): boolean {
        if (!this.isMapPresent()) {
            return false;
        }

        return source.style.every((style: MapboxStyle) => {
            const layerId = this.createLayerId(source.sourceId, style.id);

            if (!this.map.getLayer(layerId)) {
                return false;
            }

            return this.map.getLayoutProperty(layerId, 'visibility') === 'visible';
        });
    }

    public resetFilter(): void {
        for (const [layerId, savedFilter] of this.defaultStyleFilterByLayer.entries()) {
            if (!this.mapLayerService.layerExists(layerId)) {
                this.defaultStyleFilterByLayer.delete(layerId);
                continue;
            }
            this.map?.setFilter(layerId, savedFilter as ExpressionFilterSpecification);
        }

        this.defaultStyleFilterByLayer.clear();
    }

    public override toggleSourceVisibility(source: MapFieldInterventionSource): void {
        super.toggleSourceVisibility(source);
    }

    protected async waitForStyleLoaded(): Promise<void> {
        if (!this.map || this.map.isStyleLoaded()) {
            return;
        }

        await new Promise<void>((resolve) => {
            this.map!.once('idle', () => resolve());
        });
    }

    private applyFieldInterventionFilter(layerIds: string[], paramsFilter: FilterEntitiesDto): void {
        const allLayerIds =
            this.map?.getStyle().layers?.map((layerSpecification: LayerSpecification) => layerSpecification.id) ?? [];
        const matchingLayerIds = layerIds.flatMap((layerId) => allLayerIds.filter((id) => id.startsWith(layerId)));

        const filters = this.getFiltersToApply(paramsFilter);

        matchingLayerIds.forEach((layerId: string) => {
            const baseFilter = this.getBaseFilter(layerId);

            const newFilter: ExpressionFilterSpecification = baseFilter
                ? (['all', baseFilter, ...filters] as ExpressionFilterSpecification)
                : (['all', ...filters] as ExpressionFilterSpecification);

            this.map?.setFilter(layerId, newFilter);
        });
    }

    private draw(
        sourceId: string,
        geojson: GeoJSON,
        bbox: LngLatLike,
        style: MapboxStyle[],
        isVisible: boolean,
        beforeId?: string,
    ): void {
        this.addGeoJSONSource(sourceId, geojson as GeoJSON);
        this.addBaseSourceLayer(sourceId, beforeId);
        this.addSourceStyle(sourceId, style, beforeId);
        this.setSourceVisibility(sourceId, style, isVisible);

        if (this.mapBounds && isVisible) {
            this.mapBounds.extend(bbox);
        }
    }

    private async drawFieldInterventionSource(
        id: string,
        style: MapboxStyle[],
        isVisible: boolean,
        fieldIntervention: FieldIntervention,
        options: DrawSourcesOptions,
        layerId?: string,
    ): Promise<void> {
        const currentDrawGeneration = this.drawGeneration;
        let geojsons: FeatureCollection[];

        this.addBaseSourceLayer(id, options.beforeId);

        try {
            geojsons = await firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGetGeoJson(
                    {
                        fieldInterventions: [fieldIntervention.id],
                        geohashes: options.geohashes,
                        layerId: layerId,
                        project: fieldIntervention.project,
                    },
                    'body',
                    true,
                    {
                        context: new HttpContext().set(BYPASS_INTERCEPTOR, ['httpErrorsInterceptor']),
                    },
                ),
            );
        } catch {
            return;
        }

        if (currentDrawGeneration !== this.drawGeneration) {
            return;
        }

        const features = geojsons.flatMap((featureCollection: FeatureCollection) => featureCollection.features);

        const featureCollection: FeatureCollection = {
            features: features,
            type: FeatureCollection.TypeEnum.FEATURE_COLLECTION,
        };

        if (!this.isMapPresent() || !featureCollection.features.length) {
            return;
        }

        if (currentDrawGeneration !== this.drawGeneration) {
            return;
        }

        featureCollection.bbox = turfBbox(featureCollection as unknown as GeoJsonFeatureCollection, {
            recompute: true,
        });

        this.draw(
            id,
            featureCollection as GeoJSON,
            featureCollection.bbox as LngLatLike,
            style,
            isVisible,
            options.beforeId,
        );
    }

    private getBaseFilter(layerId: string): ExpressionFilterSpecification | undefined {
        const current: ExpressionFilterSpecification | undefined = this.map?.getFilter(layerId) as
            ExpressionFilterSpecification | undefined;

        if (!this.defaultStyleFilterByLayer.has(layerId)) {
            this.defaultStyleFilterByLayer.set(layerId, current);
        }

        const original: ExpressionFilterSpecification | undefined = this.defaultStyleFilterByLayer.get(layerId);

        this.map?.setFilter(layerId, original);

        return original;
    }

    private getFiltersToApply(paramsFilter: FilterEntitiesDto): ExpressionFilterSpecification[] {
        const filters: ExpressionFilterSpecification[] = [];

        if (paramsFilter.forms?.length) {
            filters.push(['==', ['get', 'hasRelevantFormId'], true]);
        }

        if (paramsFilter.people?.length) {
            filters.push(['==', ['get', 'hasRelevantPeopleId'], true]);
        }

        if (paramsFilter.statuses?.length) {
            filters.push(['in', ['get', 'status'], ['literal', paramsFilter.statuses]]);
        }

        if (paramsFilter.entityIds?.length) {
            filters.push(['match', ['get', 'entityId'], [...new Set(paramsFilter.entityIds)], true, false]);
        }

        return filters;
    }

    private async setPropertiesFilter(
        sources: MapBaseSource<unknown>[],
        paramsFilter: FilterEntitiesDto,
    ): Promise<void> {
        for (const source of sources) {
            const mapLibreSource = this.map?.getSource(source.sourceId) as GeoJSONSource;
            const data: GeoJSON = await mapLibreSource.getData();
            AssertService.assertFeatureCollectionType(data);

            data.features.forEach((feature: Feature<Geometry, GeoJsonProperties>) => {
                if (!feature.properties) {
                    return;
                }

                const featureFormIds: string[] = feature.properties?.['formIds'] ?? [];
                const featurePeopleIds: string[] = feature.properties?.['peopleIds'] ?? [];

                if (paramsFilter.forms?.length) {
                    feature.properties['hasRelevantFormId'] = featureFormIds.some((formId) =>
                        paramsFilter.forms!.includes(formId),
                    );
                }

                if (paramsFilter.people?.length) {
                    const hasRelevantPeopleId = featurePeopleIds.some((peopleId) =>
                        paramsFilter.people!.includes(peopleId),
                    );

                    feature.properties['hasRelevantPeopleId'] = hasRelevantPeopleId;
                }

                return;
            });

            mapLibreSource.setData(data);
        }
    }
}
